<template>
  <content-panel class="image-upload-task-page">
    <template v-slot:search>
      <search-box class="search-box">
        <el-form :inline="true" size="mini" label-width="90px" :model="searchForm">
          <el-form-item label="批次编号">
            <el-input v-model.trim="searchForm.taskNo" clearable placeholder="请输入批次编号" />
          </el-form-item>
          <el-form-item label="关键字">
            <el-input v-model.trim="searchForm.keyword" clearable placeholder="请输入子任务关键字" />
          </el-form-item>
          <el-form-item label="发起人">
            <el-input v-model.trim="searchForm.initiator" clearable placeholder="请输入发起人" />
          </el-form-item>
          <el-form-item label="创建时间">
            <el-date-picker
              v-model="creationDateRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              clearable
            />
          </el-form-item>
          <el-form-item label="平台">
            <platform-group-select
              ref="platformGroupSelect"
              v-model="searchForm.groupCode"
              placeholder="请选择平台"
              @change="handleGroupCodeChange"
            />
          </el-form-item>
          <el-form-item label="店铺">
            <el-select v-model="searchForm.storeCode" clearable filterable placeholder="请选择店铺">
              <el-option
                v-for="item in storeOptions"
                :key="item.storeCode"
                :label="item.storeName"
                :value="item.storeCode"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="图片类型">
            <el-select v-model="searchForm.imageType" clearable filterable placeholder="请选择图片类型">
              <el-option
                v-for="item in imageTypeOptions"
                :key="item.code"
                :label="item.description"
                :value="item.code"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="任务状态">
            <el-select v-model="searchForm.status" clearable filterable placeholder="请选择任务状态">
              <el-option
                v-for="item in taskStatusOptions"
                :key="item.code"
                :label="item.description"
                :value="item.code"
              />
            </el-select>
          </el-form-item>
        </el-form>
        <template v-slot:right>
          <el-button type="primary" size="mini" @click="handleSearch">搜索</el-button>
          <el-button class="orange-btn" size="mini" @click="resetSearch">重置</el-button>
        </template>
      </search-box>
    </template>

    <table-panel>
      <template v-slot:header-left>
        <div class="summary-tag-row">
          <el-tag type="info">子任务总数 {{ subTaskStatistics.totalCount }}</el-tag>
          <el-tag type="success">已完成 {{ subTaskStatistics.completedCount }}</el-tag>
          <el-tag>处理中 {{ subTaskStatistics.processingCount }}</el-tag>
          <el-tag type="danger">失败 {{ subTaskStatistics.failedCount }}</el-tag>
        </div>
      </template>

      <div class="table-section">
        <div class="section-title">父任务批次</div>
        <el-table
          v-loading="parentLoading"
          :data="parentTaskRows"
          border
          stripe
          size="mini"
          row-key="id"
          highlight-current-row
          :header-cell-style="{ backgroundColor: '#F4F8FB', textAlign: 'center' }"
          :cell-style="{ textAlign: 'center' }"
          @row-click="handleParentTaskClick"
        >
          <el-table-column prop="taskNo" label="批次编号" min-width="170" />
          <el-table-column prop="creationDate" label="创建时间" min-width="160" />
          <el-table-column prop="initiator" label="发起人" min-width="100" />
          <el-table-column label="状态" min-width="100">
            <template slot-scope="{ row }">
              {{ getTaskStatusDescription(row.status) }}
            </template>
          </el-table-column>
          <el-table-column prop="targetCount" label="总目标" min-width="90" />
          <el-table-column prop="totalCount" label="子任务" min-width="90" />
          <el-table-column prop="successCount" label="成功" min-width="90" />
          <el-table-column prop="processingCount" label="处理中" min-width="90" />
          <el-table-column prop="failedCount" label="失败" min-width="90" />
          <el-table-column prop="blockedCount" label="阻断" min-width="90" />
          <el-table-column prop="rolledBackCount" label="回滚" min-width="90" />
          <el-table-column prop="lastUpdateDate" label="更新时间" min-width="160" />
          <el-table-column label="操作" min-width="100" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" @click.stop="handleParentTaskClick(row)">查看任务</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          class="table-pagination"
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="parentPagination.pageNum"
          :page-size="parentPagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="parentPagination.total"
          @size-change="handleParentSizeChange"
          @current-change="handleParentCurrentChange"
        />
      </div>

      <div class="table-section">
        <div class="section-title">未进入上传队列的阻断目标</div>
        <el-table
          v-loading="blockedLoading"
          :data="blockedTargetRows"
          border
          stripe
          size="mini"
          :header-cell-style="{ backgroundColor: '#F4F8FB', textAlign: 'center' }"
          :cell-style="{ textAlign: 'center' }"
        >
          <el-table-column prop="taskNo" label="父批次" min-width="170" />
          <el-table-column prop="groupName" label="平台组" min-width="110" />
          <el-table-column prop="storeName" label="店铺" min-width="130" />
          <el-table-column prop="modelName" label="商品型号" min-width="180" show-overflow-tooltip />
          <el-table-column prop="imageType" label="图片类型" min-width="120" />
          <el-table-column prop="reasonMessage" label="阻断原因" min-width="280" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <span class="blocked-reason">{{ row.reasonMessage }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="table-section">
        <div class="section-title">处理子任务</div>
        <el-table
          v-loading="subTaskLoading"
          :data="subTaskRows"
          border
          stripe
          size="mini"
          :header-cell-style="{ backgroundColor: '#F4F8FB', textAlign: 'center' }"
          :cell-style="{ textAlign: 'center' }"
        >
          <el-table-column prop="taskNo" label="任务编号" min-width="170" />
          <el-table-column prop="parentTaskId" label="父任务ID" min-width="100" />
          <el-table-column label="商品型号/平台商品编码" min-width="190">
            <template slot-scope="{ row }">
              <span class="multiline-cell-text">{{ formatMultilineText(row.modelName, row.goodsCode) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="平台组/店铺" min-width="170">
            <template slot-scope="{ row }">
              <span class="multiline-cell-text">{{ formatMultilineText(row.groupName, row.storeName) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="platformGroupCode" label="平台组CODE" min-width="120" />
          <el-table-column prop="storeCode" label="店铺CODE" min-width="120" />
          <el-table-column prop="imageType" label="图片类型" min-width="120" />
          <el-table-column label="状态" min-width="100">
            <template slot-scope="{ row }">
              {{ getTaskStatusDescription(row.status) }}
            </template>
          </el-table-column>
          <el-table-column prop="retryCount" label="重试次数" min-width="90" />
          <el-table-column prop="revision" label="修订号" min-width="90" />
          <el-table-column prop="lastErrorCode" label="最近错误码" min-width="120" show-overflow-tooltip />
          <el-table-column prop="lastErrorMessage" label="最近错误信息" min-width="180" show-overflow-tooltip />
          <el-table-column prop="creationDate" label="创建时间" min-width="160" />
          <el-table-column prop="lastUpdateDate" label="更新时间" min-width="160" />
          <el-table-column label="操作" min-width="190" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" @click="openDetailDrawer(row)">详情</el-button>
              <el-button
                v-if="isFailedSubTask(row)"
                type="text"
                @click="handleSubTaskRetry(row)"
              >重试</el-button>
              <!-- v-if="isRollbackableSubTask(row)" -->
              <el-button
                type="text"
                @click="handleSubTaskRollback(row)"
              >回滚</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          class="table-pagination"
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="subTaskPagination.pageNum"
          :page-size="subTaskPagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="subTaskPagination.total"
          @size-change="handleSubTaskSizeChange"
          @current-change="handleSubTaskCurrentChange"
        />
      </div>
    </table-panel>

    <task-detail-drawer
      v-if="detailDrawerVisible"
      :visible.sync="detailDrawerVisible"
      :task-detail="currentTaskDetail"
      @preview="previewDialogVisible = true"
    />

    <processed-preview-dialog
      v-if="previewDialogVisible"
      :visible.sync="previewDialogVisible"
      :task-detail="currentTaskDetail"
    />
  </content-panel>
</template>

<script>
import PlatformGroupSelect from '../platformImageRuleConfig/components/PlatformGroupSelect.vue'
import TaskDetailDrawer from './components/TaskDetailDrawer.vue'
import ProcessedPreviewDialog from './components/ProcessedPreviewDialog.vue'

export default {
  name: 'ImageUploadTask',
  components: {
    PlatformGroupSelect,
    TaskDetailDrawer,
    ProcessedPreviewDialog
  },
  data () {
    return {
      creationDateRange: [],
      searchForm: {
        taskNo: '',
        initiator: '',
        status: '',
        groupCode: '',
        storeCode: '',
        imageType: '',
        keyword: ''
      },
      storeOptions: [],
      imageTypeOptions: [],
      taskStatusOptions: [],
      parentTaskRows: [],
      subTaskRows: [],
      blockedTargetRows: [],
      selectedParentTask: null,
      subTaskStatistics: {
        completedCount: 0,
        failedCount: 0,
        processingCount: 0,
        totalCount: 0
      },
      parentPagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      subTaskPagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      parentLoading: false,
      subTaskLoading: false,
      blockedLoading: false,
      detailDrawerVisible: false,
      previewDialogVisible: false,
      currentTaskDetail: null
    }
  },
  mounted () {
    this.fetchSelectOptions()
    this.fetchStoreOptions()
    this.queryParentTaskPage()
  },
  methods: {
    async fetchSelectOptions () {
      const [taskStatusRes, imageTypeRes] = await Promise.all([
        this.$q.newProduct.imageTaskStatusOption(),
        this.$q.newProduct.imageTaskImageTypeOption()
      ])
      this.taskStatusOptions = taskStatusRes.data || []
      this.imageTypeOptions = imageTypeRes.data || []
    },
    async fetchStoreOptions () {
      const groupName = this.getSelectedGroupName()
      const res = await this.$q.newProduct.imageTaskListAllStores({
        params: {
          groupName
        }
      })
      this.storeOptions = res.data || []
    },
    getSelectedGroupName () {
      const groupCode = this.searchForm.groupCode
      if (!groupCode || !this.$refs.platformGroupSelect) return ''
      const target = this.$refs.platformGroupSelect.options.find(item => item.groupCode === groupCode)
      return target ? target.groupName : ''
    },
    handleGroupCodeChange () {
      this.searchForm.storeCode = ''
      this.fetchStoreOptions()
    },
    handleSearch () {
      this.parentPagination.pageNum = 1
      this.queryParentTaskPage()
    },
    async queryParentTaskPage () {
      this.parentLoading = true
      try {
        const res = await this.$q.newProduct.imageTaskListParentTask({
          data: {
            pageNum: this.parentPagination.pageNum,
            pageSize: this.parentPagination.pageSize,
            filterData: {
              endDate: this.creationDateRange && this.creationDateRange.length ? this.creationDateRange[1] : '',
              initiator: this.searchForm.initiator,
              startDate: this.creationDateRange && this.creationDateRange.length ? this.creationDateRange[0] : '',
              status: this.searchForm.status,
              taskNo: this.searchForm.taskNo
            }
          }
        })
        const data = res.data || {}
        this.parentTaskRows = data.list || []
        this.parentPagination.total = data.total || 0
        const firstRow = this.parentTaskRows[0] || null
        if (firstRow) {
          this.handleParentTaskClick(firstRow)
        } else {
          this.selectedParentTask = null
          this.subTaskRows = []
          this.blockedTargetRows = []
          this.resetSubTaskStatistics()
        }
      } finally {
        this.parentLoading = false
      }
    },
    handleParentTaskClick (row) {
      this.selectedParentTask = row
      this.subTaskPagination.pageNum = 1
      this.querySubTaskPage()
      this.queryBlockTargets()
      this.querySubTaskStatistics()
    },
    async querySubTaskPage () {
      if (!this.selectedParentTask) return
      this.subTaskLoading = true
      try {
        const res = await this.$q.newProduct.imageTaskListSubTask({
          data: {
            pageNum: this.subTaskPagination.pageNum,
            pageSize: this.subTaskPagination.pageSize,
            filterData: {
              groupCode: this.searchForm.groupCode,
              imageType: this.searchForm.imageType,
              keyword: this.searchForm.keyword,
              parentTaskId: this.selectedParentTask.id,
              status: this.searchForm.status,
              storeCode: this.searchForm.storeCode
            }
          }
        })
        const data = res.data || {}
        this.subTaskRows = data.list || []
        this.subTaskPagination.total = data.total || 0
      } finally {
        this.subTaskLoading = false
      }
    },
    async queryBlockTargets () {
      if (!this.selectedParentTask) return
      this.blockedLoading = true
      try {
        const res = await this.$q.newProduct.imageTaskListBlockTargets({
          params: {
            parentTaskId: this.selectedParentTask.id
          }
        })
        this.blockedTargetRows = res.data || []
      } finally {
        this.blockedLoading = false
      }
    },
    async querySubTaskStatistics () {
      if (!this.selectedParentTask) return
      const res = await this.$q.newProduct.imageTaskSubTaskStatistics({
        params: {
          parentTaskId: this.selectedParentTask.id
        }
      })
      this.subTaskStatistics = res.data || {
        completedCount: 0,
        failedCount: 0,
        processingCount: 0,
        totalCount: 0
      }
    },
    resetSubTaskStatistics () {
      this.subTaskStatistics = {
        completedCount: 0,
        failedCount: 0,
        processingCount: 0,
        totalCount: 0
      }
    },
    resetSearch () {
      this.creationDateRange = []
      this.searchForm = {
        taskNo: '',
        initiator: '',
        status: '',
        groupCode: '',
        storeCode: '',
        imageType: '',
        keyword: ''
      }
      this.fetchStoreOptions()
      this.handleSearch()
    },
    handleParentSizeChange (pageSize) {
      this.parentPagination.pageSize = pageSize
      this.parentPagination.pageNum = 1
      this.queryParentTaskPage()
    },
    handleParentCurrentChange (pageNum) {
      this.parentPagination.pageNum = pageNum
      this.queryParentTaskPage()
    },
    handleSubTaskSizeChange (pageSize) {
      this.subTaskPagination.pageSize = pageSize
      this.subTaskPagination.pageNum = 1
      this.querySubTaskPage()
    },
    handleSubTaskCurrentChange (pageNum) {
      this.subTaskPagination.pageNum = pageNum
      this.querySubTaskPage()
    },
    getTaskStatusDescription (status) {
      const target = this.taskStatusOptions.find(item => item.code === status)
      return target ? target.description : status
    },
    formatMultilineText (first, second) {
      return [first, second].filter(item => item !== undefined && item !== null && item !== '').join('\n')
    },
    isFailedSubTask (row) {
      return this.getTaskStatusDescription(row.status) === '失败'
    },
    isRollbackableSubTask (row) {
      return this.getTaskStatusDescription(row.status) === '已完成'
    },
    getTaskOperationPayload (row) {
      return {
        expectedRevision: row.revision,
        operationKey: '',
        reason: ''
      }
    },
    async handleSubTaskRetry (row) {
      const res = await this.$q.newProduct.imageTaskRetrySubTask({
        fragment: [row.id, 'retry'],
        data: this.getTaskOperationPayload(row)
      })
      if (res.success) {
        this.$message.success('重试成功')
        await this.querySubTaskPage()
        await this.querySubTaskStatistics()
        await this.queryParentTaskPage()
      } else {
        this.$message.error(res.message || '重试失败')
      }
    },
    async handleSubTaskRollback (row) {
      const confirmed = await this.$confirm('确认回滚该子任务吗？', '提示', {
        type: 'warning'
      }).catch(() => false)
      if (!confirmed) return
      const res = await this.$q.newProduct.imageTaskRollbackSubTask({
        fragment: [row.id, 'rollback'],
        data: this.getTaskOperationPayload(row)
      })
      if (res.success) {
        this.$message.success('回滚成功')
        await this.querySubTaskPage()
        await this.querySubTaskStatistics()
        await this.queryParentTaskPage()
      } else {
        this.$message.error(res.message || '回滚失败')
      }
    },
    openDetailDrawer (row) {
      this.currentTaskDetail = row
      this.detailDrawerVisible = true
    }
  }
}
</script>

<style lang="stylus" scoped>
.image-upload-task-page
  .search-box
    >>> .el-select,
    >>> .el-input
      width 220px !important
    >>> .el-date-editor
      width 260px !important

.summary-tag-row
  display flex
  flex-wrap wrap
  gap 10px

.table-section
  margin-bottom 18px

.section-title
  font-size 14px
  font-weight 600
  margin-bottom 10px

.table-pagination
  margin-top 10px
  text-align right

.blocked-reason
  color #f56c6c

.multiline-cell-text
  white-space pre-line
  word-break break-all
</style>
